import React, { useMemo, useContext } from "react";
import { Link as LinkBase } from "react-router-dom";
import {
  getChannelIcons,
  getFormattedCampaignEndDate,
  getFormattedCampaignStartDate,
  getStatusCode,
} from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import { GlobalContext } from "@/helpers/contexts/contexts";
import LearnMoreButtonBase from "@/components/muiCustom/buttons/LearnMoreButton";
import StatusChipBase from "@/components/muiCustom/chip/StatusChip";
import RpImageTextInlineBase from "@/components/RpImageTextInline";
import TableServerBase from "@/components/tables/TableServer";
import TableClientBase from "@/components/tables/TableClient";
import { PauseLocationAddIcon as PauseLocationAddIconBase } from "@/features/campaigns/digital/pause-campaign";
import { MUICustomAutocompleteSelect as MUICustomAutocompleteSelectBase } from "@/components/muiCustom/select/MUICustomAutocompleteSelect";
import { CAMPAIGN_CONST, USER_ROLE } from "@/config/constants";

type CampaignRow = {
  id?: string | number;
  campaign_name?: string;
  campaign_status?: string;
  system_generated_campaign_id?: string;
  campaign_type?: string;
  participant_template?: boolean;
  brand_owner_template?: boolean;
  parent_company_template?: boolean;
  campaignBudget?: number | string | null;
  start_date?: string;
  end_date?: string;
  channelNames?: string;
  pause_campaign_available?: boolean;
};

type BrandDigitalCampaignListProps = {
  campaignReportList?: CampaignRow[];
  pageCount?: number;
  fetchCampaignsReportData: (_filters: Record<string, unknown>) => void;
  isLoading?: boolean;
  showFilter?: boolean;
  fromPage?: string;
  extraData?: {
    locationSysId?: string | number;
  };
  hideSearchLabel?: boolean;
  totalRecords?: number;
};

type GlobalCampaignContext = {
  loggedInUserRole?: string;
  loggedInUser?: {
    email?: string;
  };
  selectedBrand?: {
    status?: boolean | number;
  } | null;
};

type TableCellArgs = {
  row: {
    original: CampaignRow;
  };
};

type FilterOption = {
  id?: string;
  value?: string;
};

const Link = LinkBase as React.ComponentType<any>;
const LearnMoreButton = LearnMoreButtonBase as React.ComponentType<any>;
const StatusChip = StatusChipBase as React.ComponentType<any>;
const RpImageTextInline = RpImageTextInlineBase as React.ComponentType<any>;
const TableServer = TableServerBase as React.ComponentType<any>;
const TableClient = TableClientBase as React.ComponentType<any>;
const PauseLocationAddIcon =
  PauseLocationAddIconBase as React.ComponentType<any>;
const MUICustomAutocompleteSelect =
  MUICustomAutocompleteSelectBase as React.ComponentType<any>;
const getCampaignStartDate = getFormattedCampaignStartDate as (
  _campaign: CampaignRow,
  _context: Record<string, unknown>
) => string;
const getCampaignEndDate = getFormattedCampaignEndDate as (
  _campaign: CampaignRow,
  _context: Record<string, unknown>
) => string;
const getCampaignStatusCode = getStatusCode as (
  _status: string,
  _fallback: string
) => string;
const getCampaignChannelIcons = getChannelIcons as (
  _channels: string[]
) => React.ReactNode;

const BrandDigitalCampaignList = ({
  campaignReportList = [],
  pageCount = 0,
  fetchCampaignsReportData,
  isLoading = false,
  showFilter = false,
  fromPage = "",
  extraData = {},
  hideSearchLabel = true,
  totalRecords,
}: BrandDigitalCampaignListProps) => {
  const { loggedInUserRole, loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as GlobalCampaignContext;

  const campaignsList = useMemo(() => {
    if (
      [
        "sarah.dion@thetenspot.com",
        "angela.casullo@thetenspot.com",
        "jessica.scott@thetenspot.com",
      ].includes(loggedInUser?.email || "")
    ) {
      return campaignReportList?.filter(
        (c: CampaignRow) => c.system_generated_campaign_id !== "FTDX7289"
      );
    }
    return campaignReportList;
  }, [campaignReportList, loggedInUser]);

  const columns = useMemo(
    () => [
      {
        Header: "Campaign Name",
        accessor: "campaign_name",
        id: "campaign_name",
        width: 205,
        sortType: "string",
        Cell: ({ row }: TableCellArgs) => {
          const {
            original: {
              id,
              campaign_name,
              campaign_status,
              system_generated_campaign_id,
              campaign_type,
              participant_template,
              brand_owner_template,
              parent_company_template,
            },
          } = row;
          let searchQuery = "";

          if (
            fromPage &&
            fromPage === "location_report" &&
            extraData?.locationSysId
          ) {
            searchQuery = `?locationID=${extraData?.locationSysId}`;
          }

          const isTemplateExists =
            (loggedInUserRole === USER_ROLE.PARTICIPANT.NAME &&
              participant_template) ||
            ((loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
              loggedInUserRole === USER_ROLE.BRAND_USER.NAME) &&
              brand_owner_template) ||
            ((loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
              loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
              loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME) &&
              parent_company_template);

          const showLink =
            isTemplateExists &&
            (campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN ||
              campaign_status === CAMPAIGN_CONST.STATUS.ACTIVE.NAME ||
              campaign_status === CAMPAIGN_CONST.STATUS.COMPLETED.NAME ||
              campaign_status === CAMPAIGN_CONST.STATUS.PAUSED.NAME);

          if (showLink) {
            return (
              <Link
                to={{
                  pathname: `/dashboard/digital-campaign-report/${system_generated_campaign_id}`,
                  state: {
                    campaignId: id,
                    campaignSysId: system_generated_campaign_id,
                    from: "/dashboard",
                  },
                  search: searchQuery ? searchQuery : "",
                }}
                className={`${showLink ? "main-color link" : "gray-color"}`}
              >
                {campaign_name}
              </Link>
            );
          }

          return <span className={`gray-color`}>{campaign_name}</span>;
        },
      },
      {
        Header: "Status",
        accessor: (d: CampaignRow) => d.campaign_status,
        id: "status",
        Cell: ({ row }: TableCellArgs) => {
          const {
            original: { campaign_status },
          } = row;

          const camp_status =
            campaign_status === CAMPAIGN_CONST.STATUS.ACTIVE.NAME
              ? CAMPAIGN_CONST.STATUS.ACTIVE.TEXT
              : campaign_status;

          return camp_status ? (
            <StatusChip
              label={camp_status}
              status={getCampaignStatusCode(camp_status, "hold")}
              showOnlyLabel
            />
          ) : (
            "N/A"
          );
        },
      },
      {
        Header: "Budget",
        accessor: "campaignBudget",
        Cell: ({ row }: TableCellArgs) => {
          const {
            original: { campaignBudget },
          } = row;
          return (
            <span className="fw-b">
              {campaignBudget || campaignBudget === 0 ? (
                <CurrencyFormateText value={campaignBudget} />
              ) : (
                "N/A"
              )}
            </span>
          );
        },
      },
      {
        Header: "Start Date",
        accessor: (originalRow: CampaignRow) =>
          new Date(originalRow.start_date || ""),
        id: "start_date",
        sortType: "datetime",
        Cell: ({ row }: TableCellArgs) => {
          const { original } = row;
          const sd = getCampaignStartDate(original, {
            loggedInUserRole,
            selectedBrand,
          });
          return (
            <RpImageTextInline
              svgIcon={
                sd !== CAMPAIGN_CONST.TYPE.EVERGREEN ? "campaign_calender" : ""
              }
              imageProps={{ color: "primary" }}
              text={sd}
            />
          );
        },
      },
      {
        Header: "End Date",
        accessor: (originalRow: CampaignRow) =>
          new Date(originalRow.end_date || ""),
        sortType: "datetime",
        id: "end_date",
        Cell: ({ row }: TableCellArgs) => {
          const { original } = row;
          const ed = getCampaignEndDate(original, {
            loggedInUserRole,
            selectedBrand,
          });
          return (
            <RpImageTextInline
              svgIcon={
                ed !== CAMPAIGN_CONST.TYPE.EVERGREEN ? "campaign_calender" : ""
              }
              imageProps={{ color: "primary" }}
              text={ed}
            />
          );
        },
      },
      {
        Header: "Channels",
        accessor: "channelNames",
        id: "channels",
        canSort: false,
        disableSortBy: true,
        Cell: ({ row }: TableCellArgs) => {
          const {
            original: { channelNames = "" },
          } = row;
          return getCampaignChannelIcons(channelNames.split(",").sort());
        },
      },
      {
        Header: () => <span className="">Actions</span>,
        id: "faq",
        width: 120,
        canSort: false,
        disableSortBy: true,
        Cell: ({ row }: TableCellArgs) => {
          const { original } = row;
          return (
            <div className="d-flex align-items-center justify-content-end justify-content-lg-start">
              <LearnMoreButton
                to={{
                  pathname: `/dashboard/learn-more/digital-campaign/${original?.id}`,
                  state: { campaignId: original?.id, from: "/dashboard" },
                }}
                className="mr-1 m-0 p-0"
                icon
              />
              {original?.pause_campaign_available &&
              selectedBrand &&
              selectedBrand?.status ? (
                <PauseLocationAddIcon
                  campaignId={original?.id}
                  campaign={original}
                  className="mt-1"
                  style={{ width: "24px", height: "24px" }}
                />
              ) : null}
            </div>
          );
        },
      },
    ],
    [loggedInUserRole, selectedBrand, fromPage, extraData]
  );

  const filters = (
    setFilter: (_id: string, _value: string) => void,
    selectedFilters: FilterOption[]
  ) => {
    return (
      <div className="col-lg-3 col-md-4 col-sm-6 mb-1 mt-3">
        <MUICustomAutocompleteSelect
          name="status"
          label="Status"
          selectProps={{
            onChange: (v: string) => {
              setFilter("status", v);
            },
            value:
              selectedFilters?.find((f: FilterOption) => f.id === "status")
                ?.value || "",
          }}
          options={[
            {
              label: CAMPAIGN_CONST.STATUS.ACTIVE.TEXT,
              value: CAMPAIGN_CONST.STATUS.ACTIVE.NAME,
            },
            {
              label: CAMPAIGN_CONST.STATUS.SCHEDULED.TEXT,
              value: CAMPAIGN_CONST.STATUS.SCHEDULED.NAME,
            },
            {
              label: CAMPAIGN_CONST.STATUS.COMPLETED.TEXT,
              value: CAMPAIGN_CONST.STATUS.COMPLETED.NAME,
            },
          ]}
          all
        />
      </div>
    );
  };

  if (fromPage === "location_report") {
    return (
      <>
        <TableClient
          columns={columns}
          data={campaignsList}
          showSearch={true}
          filters={filters}
          showFilter={showFilter}
          hideSearchLabel={hideSearchLabel}
          totalRecords={totalRecords}
        />
      </>
    );
  }

  return (
    <>
      <TableServer
        columns={columns}
        data={campaignsList}
        showSearch={true}
        fetchData={fetchCampaignsReportData}
        isLoading={isLoading}
        pageCount={pageCount}
        showFilter={showFilter}
        useLayout={false}
        hideSearchLabel={hideSearchLabel}
        totalRecords={totalRecords}
      />
    </>
  );
};

export default BrandDigitalCampaignList;
